import * as React from "react";
import { MaterialTable } from "../MaterialTable/MaterialTable";
import Paper from "@material-ui/core/Paper/Paper";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import { styles } from "../UniversalTable/UniversalTable.styles";
import Link from "@material-ui/core/Link/Link";
import { bytesToHumanReadable, stringComparator } from "../../../utilities/generalUtils";
import { IRemoteDataTableColumn } from "./interfaces";
import { convertDateToFullDateTime, convertNumberToDateString } from "../../../utilities/dateTimeUtils";
import Typography from "@material-ui/core/Typography/Typography";
import { useEffect } from "react";
import { OrderDetailsAccordion } from "../UniversalTable/OrderDetailsAccordion";
import { findProductByName } from "../../views/productOverview/ProductHelper";

export const getParamsFromQuery = (query: any) => {
    return {
        limit: query?.pageSize,
        page: query.page,
        ...(query.orderBy?.field && { orderBy: query.orderBy?.field }),
        ...(query.orderDirection && { orderDirection: query.orderDirection.toUpperCase() }),
        ...(query.search && { search: query.search })
    };
};

const isNotSortable = type => {
    return ["products"].includes(type);
};

const isCustomRender = type => {
    return ["link", "size", "date", "dateTime", "products"].includes(type);
};

export interface IRemoteDataTableProps extends WithStyles<typeof styles> {
    columns: IRemoteDataTableColumn[];
    onQueryChange: (query) => Promise<any>;
    emptyDataMessage?: string;
    update?: any;
}

const TableRemoteData: React.FunctionComponent<IRemoteDataTableProps> = props => {
    const tableRef = React.createRef() as any;
    const { classes } = props;

    useEffect(() => {
        tableRef.current.onQueryChange();
    }, [tableRef, props.update])

    return (
        <div className={classes.table}>
            <MaterialTable
                title=""
                tableRef={tableRef}
                components={{
                    Container: props => <Paper {...props} elevation={0} />
                }}
                columns={props.columns.map(column => ({
                    title: column.title,
                    field: column.field,
                    sorting: !isNotSortable(column.type) && !column.sortDisable,
                    ...(isCustomRender(column.type) && {
                        render: rowData => {
                            switch (column.type) {
                                case "link":
                                    const field = rowData[column.field];
                                        const hrefUsed = column.linkField;
                                        const linkParams = hrefUsed
                                            ? {
                                                href: rowData[column.linkField]
                                            }
                                            : {
                                                onClick: () => column.onClick(field)
                                            };
                                    return (
                                        <Link className={classes.link} {...linkParams}>
                                            {column.linkTitle || field}
                                        </Link>
                                    );
                                case "date":
                                    return convertNumberToDateString(rowData[column.field]);
                                case "dateTime":
                                    return convertDateToFullDateTime(rowData[column.field]);
                                case "size":
                                    return bytesToHumanReadable(rowData[column.field]);
                                case "products":
                                    const allProducts = column.allProducts;                                    
                                    function getNameForSimpleOrder(items) {
                                        if (items.length > 0) {
                                            return findProductByName(allProducts, items[0].product)?.name;
                                        }
                                        return "Custom data";
                                    }
                                    const items = rowData[column.field];
                                    return items.length > 1
                                        ? <OrderDetailsAccordion rowId={rowData["tableData"]?.id} items={items}/>
                                        : <Typography>{getNameForSimpleOrder(items)}</Typography>;
                                default:
                                    return rowData[column.field];
                            }
                        }
                    }),
                    ...(column.type === "size" && {
                        customSort: (a, b) => a[column.field] - b[column.field]
                    }),
                    ...(column.type === "dateTime" && {
                        customSort: (a, b) => (a[column.field] > b[column.field] ? 1 : -1)
                    }),
                    ...(column.type === "text" && {
                        customSort: (a, b) => stringComparator(a[column.field], b[column.field])
                    })
                }))}
                data={props.onQueryChange}
                options={{
                    draggable: false,
                    pageSize: 10,
                    pageSizeOptions: [10],
                    paginationType: "normal",
                    headerStyle: {
                        backgroundColor: "#F0F0F0"
                    },
                    searchFieldStyle: {
                        height: 37
                    },
                    emptyRowsWhenPaging: false
                }}
                localization={{
                    body: {
                        emptyDataSourceMessage: props.emptyDataMessage ? (
                            <Typography variant="h5"> {props.emptyDataMessage}</Typography>
                        ) : (
                            "No records to display"
                        )
                    }
                }}
            />
        </div>
    );
};

export default withStyles(styles)(TableRemoteData);
